import { TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { filter } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";

import { DialogNotificationSettings } from "../../dialogs/dialog-notification-settings/dialog-notification-settings";
import { NotificationButtonModel } from "./notification-button.model";

type ImpactKey = "Downtime" | "Loss of Resiliency" | "Reduced Redundancy" | "Never";

@Component({
  selector: "notification-button",
  templateUrl: "notification-button.html",
  styleUrl: "notification-button.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatTooltipModule, AngularSvgIconModule],
})
export class NotificationButton {
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);

  subscription = input.required<NotificationButtonModel>();

  private impactOverride = signal<string | null>(null);
  protected minImpact = computed(
    () => (this.impactOverride() ?? this.subscription().minimalImpactNotification ?? "Loss of Resiliency") as ImpactKey,
  );
  protected isActive = computed(() => this.minImpact() !== "Never");
  protected impactDescription = computed(() => this.impactDescriptions()[this.minImpact()]);

  private currentLang = toSignal(this.translate.onLangChange);
  private impactDescriptions = computed(() => {
    this.currentLang();
    const for_ = this.translate.instant("Notification.ReceiveNotificationsFor");
    return {
      Downtime: `${for_} ${this.translate.instant("Notification.Status.Downtime")}`,
      "Loss of Resiliency": `${for_} ${this.translate.instant("Notification.Status.LossofResiliency")}`,
      "Reduced Redundancy": `${for_} ${this.translate.instant("Notification.Status.ReducedRedundancy")}`,
      Never: `${for_} ${this.translate.instant("Notification.Status.Never")}`,
    };
  });

  openNotificationSettingsDialog() {
    this.dialog
      .open<DialogNotificationSettings, unknown, { minimalImpactNotification: string }>(DialogNotificationSettings, {
        data: {
          subscriptionId: this.subscription().subscriptionId,
          name: this.subscription().description,
          impactSetting: this.minImpact(),
        },
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((result) => this.impactOverride.set(result.minimalImpactNotification));
  }
}
